"use client"
import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { useIsServer } from "@/hooks/app/useIsServer"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { flightActions } from "@/lib/redux/features/flight/reduce"

import CalendarIcon from "@/components/icons/CalendarIcon"
import { Card, Checkbox, Col, DatePicker, Row, theme } from "antd"
import { FC } from "react"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"

type SelectDateProps = {
  label: string,
  value: any,
  isReturn?: boolean,
  onChangeReturn?: (val?: boolean) => void
  size?: 'small' | 'default',
  layout?: 'default' | 'hero',
  heroSegment?: boolean,
  compact?: boolean,
  hideItineraryToggle?: boolean,
  indexInputFlight?: any,
  onlyContent?: boolean
}

const SelectDate: FC<SelectDateProps> = ({
  label,
  onlyContent,
  size = 'default',
  layout = 'default',
  heroSegment = false,
  compact = false,
  hideItineraryToggle = false,
  isReturn,
  value,
  onChangeReturn,
}) => {
  const checkedItinerary = useAppSelector(state => !!state.flight.checkedItinerary)
  const dispatch = useAppDispatch()
  const { getValidDayjs } = useTETranslation()
  const { t, i18n } = useTranslation()
  const { token } = theme.useToken()

  const dayjsValue: any = getValidDayjs(value)
  const isSmall = size === 'small'
  const isHero = layout === 'hero'
  const isHeroBarSegment = isHero && heroSegment
  const isReturnDisabled = !!isReturn && !checkedItinerary
  const isShown = (isReturn && checkedItinerary) || !isReturn || isHero
  const showSelectedStyle = isShown && value != null && value !== ''
  const dateFormat = isHeroBarSegment && isSmall
    ? (i18n.language?.startsWith("vi") ? "ddd, D [Thg] M YYYY" : "ddd, DD MMM YYYY")
    : (isSmall ? "ddd, DD MMM" : "DD MMMM")
  const heroDatePicker = (
    <>
      {!compact && (
        <span className="home__searching--field-icon" aria-hidden>
          <CalendarIcon />
        </span>
      )}
      <DatePicker
        value={dayjsValue}
        className={`home__searching--input home__searching--input-small${showSelectedStyle ? " home__searching--date-selected" : ""}`}
        variant="borderless"
        allowClear
        disabled={isReturnDisabled}
        open={false}
        format={dateFormat}
      />
    </>
  )
  const content = isHeroBarSegment ? (
    <div
      className={`home__searching--hero-dates-segment-content${compact ? " home__searching--hero-dates-segment-content--compact" : ""}${isReturnDisabled ? " home__searching--hero-dates-segment-content--disabled" : ""}`}
    >
      {compact ? (
        <div className="home__searching--field-box home__searching--field-box--compact">
          <Text className="home__searching--field-label">{label}</Text>
          <div className="home__searching--hero-dates-value-row">
            {heroDatePicker}
          </div>
        </div>
      ) : (
        heroDatePicker
      )}
    </div>
  ) : isHero ? (
    <div className="home__searching--field-box">
      <Text className="home__searching--field-label">{label}</Text>
      <div
        className={`home__searching--field-control${!isShown ? " home__searching--field-control--hidden" : ""}${isReturnDisabled ? " home__searching--field-control--disabled" : ""}`}
      >
        {heroDatePicker}
        <FontAwesomeIcon icon={faChevronDown} className="home__searching--field-chevron" aria-hidden />
      </div>
    </div>
  ) : (
    <>
      <Row justify={'space-between'} className="home__searching--input-label" align='middle' gutter={[5, 5]} wrap={false}>
        <Col>
          {!hideItineraryToggle && (
          <Checkbox
            classNames={{
              root: "home__searching--checkbox"
            }}
            onClick={(e) => {
              onChangeReturn && onChangeReturn(!checkedItinerary)
              dispatch(flightActions.changeCheckedItinerary(!checkedItinerary))
            }}
            checked={isReturn ? checkedItinerary : !checkedItinerary}
          >
            {isReturn ? t("flight.round_trip") : t("flight.one_way")}
          </Checkbox>
          )}
        </Col>
        {/* {isReturn && (
          <Col onClick={e => e.stopPropagation()}>
            <Switch
              onChange={(e) => {
                onChangeReturn && onChangeReturn(e)
                dispatch(flightActions.changeCheckedItinerary(e))
              }}
              value={checkedItinerary}
              size='small'
              checkedChildren="Khứ hồi"
              unCheckedChildren="Khứ hồi"
              style={{ fontSize: 14 }}
            />
          </Col>
        )} */}

      </Row>
      <Row style={{...(isSmall ? { height: '2.1rem' } : { paddingBlock: '0.5rem' }), ...(isShown ? {} : {  opacity: 0 })}}>
        <Col span={24} style={isSmall ? { height: '2.1rem' } : {}}>
          <DatePicker
            value={dayjsValue}
            className={`home__searching--input${isSmall ? ' home__searching--input-small' : ''}${showSelectedStyle ? ' home__searching--date-selected' : ''}`}
            variant='borderless'
            allowClear
            open={false}
            format={isSmall ? "ddd, DD MMM" : 'DD MMMM'}
          />
        </Col>
      </Row>
      {size === 'default' && (
        <Row>
          <Text
            type={showSelectedStyle ? undefined : 'secondary'}
            style={
              dayjsValue
                ? showSelectedStyle
                  ? { color: token.colorPrimary }
                  : {}
                : { visibility: 'hidden', opacity: 0 }
            }
          >
            {dayjsValue.format("dddd, YYYY")}
          </Text>
        </Row>
      )}
    </>
  )

  if (onlyContent) {
    return content
  }

  return (
    <Card
      size='small'
      className={`home__searching--date${!checkedItinerary && isReturn ? ' home__searching--date-disabled' : ''}`}
    >
      {content}
    </Card>
  )
}

export default SelectDate